"use client";

import Image from "next/image";
import React, { act, useCallback, useEffect, useRef, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useSearchListQuery, useTermListQuery } from "@/Redux/apis/product";
import axios from "axios";
import { debounce } from "@/utils/common";
import {
  useAddSearchHistoryMutation,
  useSearchHistoryQuery,
} from "@/Redux/apis/auth";
import { useSelector } from "react-redux";
import Link from "next/link";
import { GoHeart, GoHeartFill, GoSearch } from "react-icons/go";
import SearchHistoryItem from "./SearchHistoryItem";
import { getSearchSuggestionIdentities, getSearchSuggestionIdentity, uniqueSearchSuggestions, withSearchSuggestionHistoryDisplay } from "@/utils/searchSuggestionIdentity";
import { getSearchSuggestionLabel } from "@/utils/searchSuggestionDisplay";

import { IoIosArrowRoundForward } from "react-icons/io";
import { IoArrowForward } from "react-icons/io5";
import InfiniteScroll from "react-infinite-scroll-component";
import { useProductFavourite } from "@/hooks/useFavourite";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/Components/ui/tooltip";
import RatingStar from "@/Components/Common/Icons/RatingStar";
import ProductsListingPopUp from "@/Components/Common/ProductList/ProductsListingPopUp";
import CompareToggle from "@/Components/Common/Compare/CompareToggle";
import {
  consumeSearchIntent,
  getSearchSessionId,
  getKeywordForSelection,
  getSearchSelectionContext,
  logKeywordSearch,
} from "@/utils/logKeywordSearch";

/**
 * Small inline badge that disambiguates Drug vs Biologic within the merged
 * "Drugs" tab. Returns null for any other type so we don't clutter Devices /
 * OTC / Vaccines cards (those have their own tabs and don't need a badge).
 */
/**
 * Sort a list of `{ name, approvedDate }` manufacturer records by:
 *   1) has-date first (rows with an FDA approval date come before undated rows)
 *   2) earliest approval date first (chronological)
 *   3) alphabetical (stable tiebreak when many manufacturers share the same date)
 *
 * Mirrors the rule used by `getDistinctManufacturersForBrand` on the api-server
 * so the comma-separated manufacturer line on a search-card matches the order
 * shown on the product detail page.
 */
/**
 * Pipeline drugs live at /pipeline/:slug, not /products/:type/:alias.
 * This helper picks the right route based on the row's type.
 */
const productHref = (type, alias, returnTo) => {
  if ((type || "").toLowerCase() === "pipeline") {
    const returnQuery = returnTo
      ? `?returnTo=${encodeURIComponent(returnTo)}`
      : "";
    return `/pipeline/${alias}${returnQuery}`;
  }
  return `/products/${type}/${alias}`;
};

const toCompareProduct = (product, parent = {}) => ({
  id: product?.id || product?._id || parent?.id || parent?._id,
  type: product?.type || parent?.type,
  productAlias: product?.productAlias || parent?.productAlias,
  catalogDisplayFamily: product?.catalogDisplayFamily || parent?.catalogDisplayFamily,
  productFamilyIds: product?.productFamilyIds || parent?.productFamilyIds,
  genericNames: product?.genericNames || parent?.genericNames,
  brand:
    product?.brand_name ||
    product?.openfda?.brand_name ||
    parent?.brand_name ||
    parent?.openfda?.brand_name,
  generic:
    product?.generic_name ||
    product?.openfda?.generic_name ||
    parent?.generic_name ||
    parent?.openfda?.generic_name,
  manufacturer:
    product?.manufacturer_name ||
    product?.openfda?.manufacturer_name ||
    parent?.manufacturer_name ||
    parent?.openfda?.manufacturer_name,
});

const sortManufacturersByApprovalDate = (mfrs) =>
  [...(mfrs || [])].sort((a, b) => {
    const aT = a?.approvedDate ? new Date(a.approvedDate).getTime() : NaN;
    const bT = b?.approvedDate ? new Date(b.approvedDate).getTime() : NaN;
    const aHas = Number.isFinite(aT) ? 0 : 1;
    const bHas = Number.isFinite(bT) ? 0 : 1;
    if (aHas !== bHas) return aHas - bHas;        // dated rows first
    if (aHas === 0 && aT !== bT) return aT - bT;  // earliest date first
    const an = (a?.name || '').toLowerCase();
    const bn = (b?.name || '').toLowerCase();
    return an.localeCompare(bn);                  // alphabetical tiebreak
  });

const TypeBadge = ({ type }) => {
  const t = (type || "").toLowerCase();
  if (t !== "drug" && t !== "biologic" && t !== "pipeline") return null;
  const label = t === "pipeline" ? "Pipeline" : t === "biologic" ? "Biologic" : "Drug";
  return (
    <span
      className="inline-flex items-center text-[10px] sm:text-[11px] font-semibold leading-none px-1.5 py-0.5 rounded uppercase tracking-wide flex-shrink-0 bg-blue-50 text-blue-700 border border-blue-200"
      title={label}
    >
      {label}
    </span>
  );
};

/**
 * Lifecycle badge for pipeline cards. Shown next to the standard
 * "Pipeline" type badge when a pipeline drug has moved out of the
 * "active" state — either graduated to FDA approval (green) or
 * discontinued from trials (grey). For active drugs we render nothing
 * so cards stay visually quiet.
 */
const LifecycleBadge = ({ lifecycle, type }) => {
  if ((type || "").toLowerCase() !== "pipeline") return null;
  const lc = (lifecycle || "active").toLowerCase();
  if (lc === "active") return null;
  if (lc === "graduated") {
    return (
      <span
        className="inline-flex items-center text-[10px] sm:text-[11px] font-semibold leading-none px-1.5 py-0.5 rounded uppercase tracking-wide flex-shrink-0 bg-emerald-50 text-emerald-700 border border-emerald-200"
        title="This drug has been approved by the FDA"
      >
        ✓ Now Approved
      </span>
    );
  }
  if (lc === "discontinued") {
    return (
      <span
        className="inline-flex items-center text-[10px] sm:text-[11px] font-semibold leading-none px-1.5 py-0.5 rounded uppercase tracking-wide flex-shrink-0 bg-neutral-100 text-neutral-600 border border-neutral-300"
        title="The clinical trial is no longer in active development"
      >
        Trial Discontinued
      </span>
    );
  }
  return null;
};

const SearchFilterMedication = ({ forcedTab = null }) => {
  const [addSearchHistory] = useAddSearchHistoryMutation();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const serializedSearchParams = searchParams.toString();
  const searchReturnUrl = `${pathname}${
    serializedSearchParams ? `?${serializedSearchParams}` : ""
  }`;
  const getProductHref = (type, alias) =>
    productHref(type, alias, searchReturnUrl);
  const query = searchParams.get("searchQuery");
  const search = searchParams.get("search");
  const term = searchParams.get("term");
  const category = searchParams.get("type");
  const outputVar = searchParams.get("output");
  // Optional URL hint that lets callers (e.g. Emerging & Pipeline page's
  // pipeline-specific search bar) pre-select a category tab on landing.
  // Accepts: drug | device | otc | vaccine | pipeline. Anything else
  // is ignored and the default (Drugs) is used. Pure additive: callers
  // that don't set it see the same default behavior as before.
  const initialTabHint = forcedTab || searchParams.get("tab");
  const [searchQuery, setSearchQuery] = useState(term && term !== "undefined" ? term : query || "");
  // Biologics are merged into the Drugs tab (server-side filter expands
  // `category=drug` to include both `drug` and `biologic`). Each product card
  // renders its own `type` badge so users can still distinguish them.
  const [tabs, setTabs] = useState([
    { name: "Drugs", count: 0, type: "drug" },
    { name: "Devices", count: 0, type: "device" },
    { name: "OTC", count: 0, type: "otc" },
    { name: "Vaccines", count: 0, type: "vaccine" },
    // Pipeline tab — emerging / unreleased drugs from ClinicalTrials.gov
    // (also surfaced inline in the Drug/Device/OTC/Vaccine tabs with a
    // "Pipeline" badge; this tab filters to pipeline-only results).
    { name: "Pipeline", count: 0, type: "pipeline" },
  ]);
  // Pre-select tab from `?tab=...` URL hint, falling back to "Drugs".
  const [activeTab, setActiveTab] = useState(() => {
    if (initialTabHint) {
      const hinted = [
        { name: "Drugs", count: 0, type: "drug" },
        { name: "Devices", count: 0, type: "device" },
        { name: "OTC", count: 0, type: "otc" },
        { name: "Vaccines", count: 0, type: "vaccine" },
        { name: "Pipeline", count: 0, type: "pipeline" },
      ].find((t) => t.type === initialTabHint);
      if (hinted) return hinted;
    }
    return { name: "Drugs", count: 0, type: "drug" };
  });
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [afterKey, setAfterKey] = useState(null);
  const [totalResults, setTotalResults] = useState(0);
  const [manualLoader, setManualLoader] = useState(false);
  const [termList, setTermList] = useState([]);
  const [selectedTermId, setSelectedTermId] = useState(false);
  const [searchVar, setSearchVar] = useState(search);
  const [queryVar, setQueryVar] = useState(query);
  const [categoryVar, setCategoryVar] = useState(category);
  const [productList, setProductList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const searchRequestRef = useRef(null);
  const paginationRequestRef = useRef(null);
  // Results already loaded for each tab of the current search, so switching
  // tabs is instant. `key` identifies the search (everything but the tab).
  const tabCacheRef = useRef({ key: null, byTab: {} });
  const prefetchRef = useRef(null);
  const [output, setOutput] = useState(outputVar);
  const [error, setError] = useState("");
  const [brandsModalData, setBrandsModalData] = useState(null);

  useEffect(() => () => {
    searchRequestRef.current?.controller.abort();
    searchRequestRef.current = null;
    paginationRequestRef.current?.abort();
    paginationRequestRef.current = null;
    prefetchRef.current?.controller.abort();
  }, []);

  // Read IP from localStorage synchronously — set by IpFetcher in root layout
  // Available instantly before any useEffect fires, no network call on this page
  const cachedIpRef = useRef(
    typeof window !== 'undefined' ? localStorage.getItem('user_ip') || "" : ""
  );

  // Sort tokens are the same across tabs (backend speaks one vocabulary)
  // but the LABELS for the date sorts adapt to context: on the Pipeline
  // tab, "Recently Approved" / "Earlier Approved" don't make sense
  // because pipeline drugs aren't approved yet — they're sorted by
  // trial start date instead. The backend's pipeline ES query maps the
  // same sort token (`latest_approval_desc`) to `latestStartDate` so
  // only the user-facing label needs to change here.
  const baseSortOptions = [
    {
      label: "Top Rated",
      icon: "/Images/star.svg",
      sort: "rating_desc",
    },
    {
      label: "Recently Reviewed",
      icon: "/Images/star.svg",
      sort: "recent_review_desc",
    },
    {
      label: "Recently Approved",
      pipelineLabel: "Recently Started Trial",
      icon: "/Images/RecentsIcon.svg",
      sort: "latest_approval_desc",
    },
    {
      label: "Earlier Approved",
      pipelineLabel: "Earliest Started Trial",
      icon: "/Images/DateIcon.svg",
      sort: "latest_approval_asc",
    },
    {
      label: "Name: Ascending",
      icon: "/Images/Ascending.svg",
      sort: "brand_name_asc",
    },
    {
      label: "Name: Descending",
      icon: "/Images/descending.svg",
      sort: "brand_name_desc",
    },
  ];
  const [selectedOption, setSelectedOption] = useState(baseSortOptions[0]);

  // Compute the live sort-options array. When the Pipeline tab is
  // active, swap the date-sort labels to their pipeline-specific
  // wording. The underlying `sort` token doesn't change — the backend
  // applies pipeline-meaningful sorts under the same key.
  const sortOptions = baseSortOptions.map((opt) =>
    activeTab?.type === "pipeline" && opt.pipelineLabel
      ? { ...opt, label: opt.pipelineLabel }
      : opt
  );

  // Track URL params to detect navigation to same route with different params
  // (e.g., back → new search). useState only uses initial value on mount,
  // so we must sync state when URL params change.
  const urlParamKey = `${search}|${query}|${term}|${category}`;
  const prevUrlParamKey = useRef(urlParamKey);
  const isInitialMount = useRef(true);

  useEffect(() => {
    // Always dismiss the global loader (user may be returning from a product page)


    const isParamChange = prevUrlParamKey.current !== urlParamKey;
    resetTabCache();
    if (isParamChange) {
      // URL params changed (user navigated back and searched again)
      prevUrlParamKey.current = urlParamKey;
      setSearchQuery(term && term !== "undefined" ? term : query || "");
      setSearchVar(search);
      setQueryVar(query);
      setCategoryVar(category);
      setOutput(outputVar);
      setProductList([]);
      setAfterKey(null);
      setHasMoreData(true);
      // Honor the `?tab=...` URL hint when resetting the active tab on
      // a fresh search navigation. Falls through to "Drugs" when the
      // hint is missing or unrecognized so existing callers behave
      // exactly as before.
      const freshTabs = [
        { name: "Drugs", count: 0, type: "drug" },
        { name: "Devices", count: 0, type: "device" },
        { name: "OTC", count: 0, type: "otc" },
        { name: "Vaccines", count: 0, type: "vaccine" },
        { name: "Pipeline", count: 0, type: "pipeline" },
      ];
      const hinted = initialTabHint
        ? freshTabs.find((t) => t.type === initialTabHint)
        : null;
      setActiveTab(hinted || freshTabs[0]);
      setTabs(freshTabs);
      setError("");
    }

    // Pass URL params directly to handleSearch via `data` to avoid stale state
    // (setState is async/batched, so categoryVar/queryVar/searchVar may not be updated yet)
    // ALSO bypass stale `activeTab` — when the URL hints a non-default
    // category tab via `?tab=...` the setActiveTab() above hasn't
    // applied yet (React batches state updates) so we pick the same
    // hint here for the initial fetch's `category` arg.
    const effectiveTabType = (() => {
      if (isParamChange && initialTabHint) {
        const t = ["drug", "device", "otc", "vaccine", "pipeline"].find(
          (x) => x === initialTabHint
        );
        if (t) return t;
      }
      return activeTab.type || tabs[0].type;
    })();
    if (category == "general") {
      // Only push URL on param change, not on initial mount (back navigation)
      // to avoid triggering Next.js loading overlay
      if (!isParamChange && !isInitialMount.current && !initialTabHint) {
        router.push(
          `/search/query?${generateEncodedUrl({
            searchQuery: term || searchQuery,
            term: term || searchQuery,
            type: "general",
          })}`
        );
      }
      handleSearch(
        "general",
        {
          searchQuery: term || searchQuery,
        },
        effectiveTabType,
        selectedOption.sort || sortOptions[0].sort
      );
    } else {
      handleSearch(
        category,
        {
          searchQuery: query,
          search: search,
          type: category,
          // Forward the clicked-term name from the URL so handleSearch can emit
          // the correct `searchTerm` query param (not the raw input). Regression
          // from 5b5d801 — clicked-term was read from URL but never plumbed here.
          term: term,
        },
        effectiveTabType,
        selectedOption.sort || sortOptions[0].sort
      );
    }
    isInitialMount.current = false;
  }, [urlParamKey]);

  const [finalQuery, setFinalQuery] = useState({});

  const searchKey = (q) => JSON.stringify([q.type, q.searchQuery, q.searchTerm, q.search || "", q.sort]);

  const resetTabCache = () => {
    prefetchRef.current?.controller.abort();
    prefetchRef.current = null;
    tabCacheRef.current = { key: null, byTab: {} };
  };

  const rememberTab = (q, entry) => {
    const key = searchKey(q);
    if (tabCacheRef.current.key !== key) {
      prefetchRef.current?.controller.abort();
      prefetchRef.current = null;
      tabCacheRef.current = { key, byTab: {} };
    }
    tabCacheRef.current.byTab[q.category] = entry;
  };

  // Load the first page of every other tab that has matches in the background.
  // These requests carry no search-activity id, so they aren't logged as searches.
  const prefetchOtherTabs = (q, tabsWithCounts, headers) => {
    if (initialTabHint) return;
    const key = searchKey(q);
    // Already loading the other tabs for this same search.
    if (prefetchRef.current?.key === key) return;
    const controller = new AbortController();
    prefetchRef.current?.controller.abort();
    prefetchRef.current = { controller, key };
    tabsWithCounts
      .filter((tab) => tab.count > 0 && tab.type !== q.category && !tabCacheRef.current.byTab[tab.type])
      .forEach(async (tab) => {
        try {
          const params = new URLSearchParams({
            type: q.type, page: "1", searchQuery: q.searchQuery, searchTerm: q.searchTerm,
            category: tab.type, sort: q.sort,
          });
          if (cachedIpRef.current) params.append("ip", cachedIpRef.current);
          if (q.search) params.append("search", q.search);
          const response = await axios.get(
            `${process.env.NEXT_PUBLIC_BASE_URL}/products/publicsearchlist?${params.toString()}`,
            { headers, signal: controller.signal }
          );
          if (controller.signal.aborted || tabCacheRef.current.key !== key || tabCacheRef.current.byTab[tab.type]) return;
          tabCacheRef.current.byTab[tab.type] = {
            productList: response.data.productslist || [],
            hasMore: response.data.hasMore || false,
            afterKey: response.data.nextAfterKey,
            totalResults: response.data.totalResults,
          };
        } catch {
          // Ignored — clicking the tab falls back to a normal search.
        }
      });
  };

  // Switch tabs from the cache when that tab is already loaded. Returns false
  // when it isn't, so the caller runs a normal search.
  const showCachedTab = (tab) => {
    const cache = tabCacheRef.current;
    if (cache.key !== searchKey(finalQuery)) return false;
    // Keep any extra pages loaded on the tab being left.
    if (!listLoading && cache.byTab[finalQuery.category]) {
      cache.byTab[finalQuery.category] = { productList, hasMore: hasMoreData, afterKey, totalResults };
    }
    const entry = cache.byTab[tab.type];
    if (!entry) return false;
    searchRequestRef.current?.controller.abort();
    searchRequestRef.current = null;
    paginationRequestRef.current?.abort();
    paginationRequestRef.current = null;
    setActiveTab(tab);
    setError("");
    setProductList(entry.productList);
    setHasMoreData(entry.hasMore);
    setAfterKey(entry.afterKey);
    setTotalResults(entry.totalResults);
    setFinalQuery({ ...finalQuery, category: tab.type });
    setListLoading(false);
    return true;
  };

  const startSearchRequest = () => {
    searchRequestRef.current?.controller.abort();
    paginationRequestRef.current?.abort();
    paginationRequestRef.current = null;
    const request = { controller: new AbortController(), pending: true };
    searchRequestRef.current = request;
    setListLoading(true);
    setError("");
    return request;
  };

  const handleSearch = async (type, data, activeCategory, sort) => {
    const request = startSearchRequest();
    try {
      // Reset pagination state for new search
      setAfterKey(null);
      setHasMoreData(true);
      // router.refresh();

      // Get JWT token
      const cookies = document.cookie.split(";");
      let jwtCookie = null;
      cookies.forEach((cookie) => {
        if (cookie.trim().startsWith("token=")) {
          jwtCookie = "Bearer " + cookie.split("token=")[1];
        }
      });

      const activeTabCategory = activeCategory || activeTab.type;
      const headers = {
        Authorization: jwtCookie || "",
      };

      // Always use publicsearchlist — auth header is passed for rate-limit bypass
      // so logged-in users won't hit "Free search limit reached"
      let url = `${process.env.NEXT_PUBLIC_BASE_URL}/products/publicsearchlist`;
      const queryParams = new URLSearchParams({
        type: type === "general" ? "general" : data?.type || categoryVar,
        page: "1",
        searchQuery: data ? data.searchQuery : type === "general" ? searchQuery : queryVar,
        // Prefer the clicked-term name (data.term) when present so the backend
        // receives the canonical term, not the raw typed text. Falls back to
        // data.searchQuery for general searches where no term was clicked.
        searchTerm: data?.term || (data ? data.searchQuery : searchQuery),
        category: activeTabCategory || activeTab.type || tabs[0].type,
        sort: sort
      });
      // IP fetched on page load (non-blocking), use if available
      if (cachedIpRef.current) {
        queryParams.append("ip", cachedIpRef.current);
      }
      if (data?.search || searchVar) {
        queryParams.append("search", data?.search || searchVar);
      }

      const selectedTermId = ["Diseases", "Psychiatry and Psychology"].includes(queryParams.get("type"))
        ? queryParams.get("search") : undefined;
      const eventId = consumeSearchIntent(queryParams.get("searchQuery"), selectedTermId);
      if (eventId) {
        queryParams.append("searchEventId", eventId);
        queryParams.append("searchSessionId", getSearchSessionId());
        queryParams.append("searchScope", forcedTab || initialTabHint ? "category" : "all");
      }

      url = `${url}?${queryParams.toString()}`;

      // Make the API call
      const response = await axios.get(url, { headers, signal: request.controller.signal });
      if (searchRequestRef.current !== request) return;

      // Handle pagination data
      setHasMoreData(response.data.hasMore || false);
      setTotalResults(response.data.totalResults);
      setAfterKey(response.data.nextAfterKey);
      setIsSuggestionOpen(false);

      // Update final query for pagination
      const nextFinalQuery = {
        type: type === "general" ? "general" : data?.type || categoryVar,
        searchQuery: data ? data.searchQuery : type === "general" ? searchQuery : queryVar,
        // Mirrors line 207: prefer the clicked-term name so fetchMore paginates
        // on the canonical term instead of the raw typed input.
        searchTerm: data?.term || (data ? data.searchQuery : searchQuery),
        search: data?.search || searchVar || "",
        category: activeTabCategory || activeTab.type || tabs[0].type,
        sort: sort || selectedOption.sort || sortOptions[0].sort,
      };
      setFinalQuery(nextFinalQuery);
      let tabsWithCounts = null;

      // Update tab counts from typesCounts (returned by backend for all search types)
      if (response.data.typesCounts) {
        const counts = response.data.typesCounts;
        // Backend already merges biologic into drug, but keep an extra-safety
        // sum here so a stale API response that still returns both keys still
        // renders correctly.
        const updatedTabs = [
          { name: "Drugs", count: (counts.drug || 0) + (counts.biologic || 0), type: "drug" },
          { name: "Devices", count: counts.device || 0, type: "device" },
          { name: "OTC", count: counts.otc || 0, type: "otc" },
          { name: "Vaccines", count: counts.vaccine || 0, type: "vaccine" },
          { name: "Pipeline", count: counts.pipeline || 0, type: "pipeline" },
        ];
        setTabs(updatedTabs);
        tabsWithCounts = updatedTabs;

        // A caller-provided tab hint is a hard scope. In particular, searches
        // submitted from Emerging & Pipeline must stay on Pipeline even when
        // another category also has matches. `activeTab` can still contain
        // the previous render's value here, so use the category that powered
        // this request instead of that stale state.
        const scopedTab = initialTabHint
          ? updatedTabs.find((tab) => tab.type === initialTabHint)
          : null;
        const currentTabCount = counts[activeTabCategory] || 0;
        if (scopedTab) {
          setActiveTab(scopedTab);
        } else if (currentTabCount === 0 && response.data.productslist?.length === 0) {
          const firstWithData = updatedTabs.find((t) => t.count > 0);
          if (firstWithData) {
            setActiveTab(firstWithData);
            // Only re-fetch if we got 0 results (need different category filter)
            return await handleSearch(type, data, firstWithData.type, sort);
          }
        } else if (currentTabCount === 0) {
          // Tab has 0 count but we got results — just switch tab, no re-fetch
          const firstWithData = updatedTabs.find((t) => t.count > 0);
          if (firstWithData) {
            setActiveTab(firstWithData);
          }
        }
      }

      // Rating data now comes directly from backend - no need for separate API calls
      setProductList(response.data.productslist);
      rememberTab(nextFinalQuery, {
        productList: response.data.productslist || [],
        hasMore: response.data.hasMore || false,
        afterKey: response.data.nextAfterKey,
        totalResults: response.data.totalResults,
      });
      if (tabsWithCounts) prefetchOtherTabs(nextFinalQuery, tabsWithCounts, headers);
    } catch (err) {
      if (searchRequestRef.current !== request || request.controller.signal.aborted) return;
      setError(err?.response?.data?.message || "An error occurred while searching");
      setProductList([]);
      setHasMoreData(false);
      setTotalResults(0);
    } finally {
      // A category fallback or newer search owns loading until its request ends.
      if (searchRequestRef.current === request) {
        request.pending = false;
        setListLoading(false);
        setIsClickLoading(false);
      }
    }
  };
  const fetchMore = async () => {
    if (listLoading || searchRequestRef.current?.pending || paginationRequestRef.current || !hasMoreData || !finalQuery || !afterKey) return;
    const searchRequest = searchRequestRef.current;
    const controller = new AbortController();
    paginationRequestRef.current = controller;

    try {
      // Get JWT token
      const cookies = document.cookie.split(";");
      let jwtCookie = null;
      cookies.forEach((cookie) => {
        if (cookie.trim().startsWith("token=")) {
          jwtCookie = "Bearer " + cookie.split("token=")[1];
        }
      });

      const headers = {
        Authorization: jwtCookie || "",
      };

      const activeTabCategory = finalQuery.category || activeTab.type;

      // Always use publicsearchlist — auth header is passed for rate-limit bypass
      let url = `${process.env.NEXT_PUBLIC_BASE_URL}/products/publicsearchlist`;
      const queryParams = new URLSearchParams({
        type: finalQuery.type,
        searchQuery: finalQuery.searchQuery,
        searchTerm: finalQuery.searchTerm,
        category: activeTabCategory,
        sort: finalQuery.sort,
        afterKey: afterKey
      });
      if (cachedIpRef.current) {
        queryParams.append("ip", cachedIpRef.current);
      }
      if (finalQuery.search) {
        queryParams.append("search", finalQuery.search);
      }

      url = `${url}?${queryParams.toString()}`;

      // Make the API call
      const response = await axios.get(url, { headers, signal: controller.signal });
      if (searchRequestRef.current !== searchRequest || controller.signal.aborted) return;

      // Update pagination state
      setHasMoreData(response.data.hasMore || false);
      setAfterKey(response.data.nextAfterKey);

      // Rating data now comes directly from backend - no need for separate API calls
      setProductList(prevList => [...prevList, ...response.data.productslist]);
    } catch (error) {
      if (searchRequestRef.current !== searchRequest || controller.signal.aborted) return;
      console.error("Error fetching more results:", error);
      // Don't reset the product list on pagination error
      setHasMoreData(false);
    } finally {
      if (paginationRequestRef.current === controller) {
        paginationRequestRef.current = null;
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = (option) => {
    resetTabCache();
    setSelectedOption(option);
    // Reset pagination state when sort changes
    setAfterKey(null);
    setHasMoreData(true);
    setProductList([]);

    if (category != "general") {
      handleSearch(category, null, activeTab.type || tabs[0].type, option.sort);
    } else {
      router.push(buildGeneralSearchUrl(searchQuery));
      handleSearch(
        "general",
        {
          searchQuery,
        },
        activeTab.type || tabs[0].type,
        option.sort
      );
    }
    setIsOpen(false);
  };
  const users = useSelector((state) => state?.userData?.userData?.user);
  const {
    data: termData,
    error: termError,
    isLoading: isTermLoading,
  } = useTermListQuery(
    {
      search: searchQuery,
      page: 1,
      type: initialTabHint === "pipeline" ? "pipeline" : "all",
      loggedIn: users != null,
    },
    {
      skip: searchQuery?.trim()?.length < 0,
    }
  );

  useEffect(() => {
    if (termData && !isTermLoading) {
      console.log(termData);
      setManualLoader(false);
      setTermList(termData.productslist || []);
    }
  }, [termData]);

  const [isSuggestionOpen, setIsSuggestionOpen] = useState(false);
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !inputRef.current.contains(event.target)
      ) {
        setIsSuggestionOpen(false);
        setTermList([]);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const { isFavourite: isFavouriteProduct, toggleFavourite: toggleProductFavourite } = useProductFavourite();
  const searchHistoryAPI = useSearchHistoryQuery(users?._id, {
    skip: !users?._id
  });
  const [searchHistoryLoading, setSearchHistoryLoading] = useState(true);
  const [searchHistory, setSearchHistory] = useState([]);
  useEffect(() => {
    if (searchHistoryAPI?.isLoading || searchHistoryAPI?.isFetching) {
      setSearchHistoryLoading(true);
    } else if (!searchHistoryAPI.isLoading && searchHistoryAPI.isSuccess) {
      setSearchHistoryLoading(false);
      setSearchHistory(searchHistoryAPI.data.history);
    } else if (searchHistoryAPI?.isError) {
      setSearchHistoryLoading(false);
    } else {
      setSearchHistoryLoading(false);
    }
  }, [searchHistoryAPI]);
  const generateEncodedUrl = (params) => {
    const queryString = Object.keys(params)
      .map(
        (key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`
      )
      .join("&");
    return queryString;
  };
  const scopedSearchPath =
    pathname === "/search/pipeline" ? "/search/pipeline" : "/search/query";
  const buildGeneralSearchUrl = (value) => {
    const params = {
      searchQuery: value,
      term: value,
      type: "general",
    };
    if (initialTabHint && scopedSearchPath !== "/search/pipeline") {
      params.tab = initialTabHint;
    }
    const returnTo = searchParams.get("returnTo");
    if (returnTo) params.returnTo = returnTo;
    return `${scopedSearchPath}?${generateEncodedUrl(params)}`;
  };
  const router = useRouter();

  const handleClick = async (data, from) => {
    if (from !== "history") {
      void logKeywordSearch(getKeywordForSelection(searchQuery, data), getSearchSelectionContext(data, "results"));
    }

    let type = data.type;
    setActiveTab(tabs[0]);

    // Set loading state
    setIsClickLoading(true);
    if (
      type === "drug" ||
      type == "biologic" ||
      type == "vaccine" ||
      type == "otc" ||
      type == "device"
    ) {
      console.log("DATA", data);
      if (from === "history") {
        try {
          await router.push(`/products/${type}/${data.clickedProduct.productAlias}`);
        } finally {
          setIsClickLoading(false);

        }
      } else {
        try {
          await router.push(`/products/${type}/${data.productAlias}`);
        } finally {
          setIsClickLoading(false);

        }
      }
      return;
    }
    // Pipeline rows route to /pipeline/:slug (no `type` segment) — they
    // aren't in the Products collection so /products/pipeline/X 404s.
    if (type === "pipeline") {
      const slug = from === "history"
        ? data.clickedProduct?.productAlias || data.clickedProduct?.slug
        : data.productAlias || data.slug;
      if (slug) {
        try {
          await router.push(getProductHref("pipeline", slug));
        } finally {
          setIsClickLoading(false);
        }
        return;
      }
    }
    const isDiagnosis = type === "Diseases"
      || type === "diseases"
      || type === "Disease"
      || type === "Psychiatry and Psychology";
    if (from !== "history" && isDiagnosis) {
      await addSearchHistory({
        type,
        searchQuery,
        result: data,
      }).unwrap();
    }
    // Build URL based on source — the actual search is triggered by the useEffect
    // when URL params change after router.push
    let url = "";
    if (from === "history") {
      url = `/search/query?${generateEncodedUrl({
        search:
          data.type === "manufacturer"
            ? data.clickedProduct.productName
            : data.id || data.clickedProduct.productId,
        searchQuery:
          data.type === "manufacturer"
            ? data.clickedProduct.productName
            : data?.clickedProduct?.productName
              ? data?.clickedProduct?.productName
              : data?.clickedProduct?.genericName,
        term:
          data.type === "Diseases" || data.type === "Psychiatry and Psychology"
            ? (data.term || data.name || data?.clickedProduct?.productName || "")
            : data.type === "manufacturer"
              ? data?.clickedProduct?.productName
              : data?.clickedProduct?.productName
                ? data?.clickedProduct?.productName
                : data?.clickedProduct?.genericName,
        type:
          data.type == "diseases"
            ? "Diseases"
            : data.type == "manufacturer"
              ? "Manufacturer"
              : data.type,
      })}`;
    } else {
      url = `/search/query?${generateEncodedUrl({
        search: data.type === "Manufacturer" ? data.name : data.id,
        searchQuery: searchQuery || "",
        term:
          data.type === "Diseases" || data.type === "Psychiatry and Psychology"
            ? (data.term || data.name || searchQuery || "")
            : data.type === "Manufacturer"
              ? data.name
              : data?.openfda?.brand_name
                ? data?.openfda?.brand_name
                : data?.openfda?.generic_name,
        type: data.type,
      })}`;
    }
    setIsSuggestionOpen(false);
    try {
      await router.push(url);
    } finally {
      setIsClickLoading(false);
  
    }
  };
  function isExactMatch() {
    return termList.some(
      (item) => item.name.toLowerCase() === searchQuery.toLowerCase()
    );
  }
  const handleFav = (e, product) => {
    toggleProductFavourite(e, product);
  };
  const [isClickLoading, setIsClickLoading] = useState(false);

  return (
    <>
      <div className="w-full h-full rounded-lg py-3 sm:py-6">
        <>
          <div className="bg-white p-3 sm:p-4 rounded-lg h-full">
            <div className="flex gap-2 sm:gap-4 items-center justify-start w-full pb-4 sm:pb-8">
              <form
                className="flex flex-col sm:flex-row w-full gap-2 sm:gap-3"
                onSubmit={async (e) => {
                  e.preventDefault();
                  const submittedQuery = searchQuery.trim();
                  if (submittedQuery) {
                    void logKeywordSearch(submittedQuery);
                    // Reset stale search vars before general search
                    setSearchVar(null);
                    setCategoryVar("general");
                    setQueryVar(submittedQuery);
                    // Reset tabs so skeleton shows while loading
                    setTabs([
                      { name: "Drugs", count: 0, type: "drug" },
                      { name: "Devices", count: 0, type: "device" },
                      { name: "OTC", count: 0, type: "otc" },
                      { name: "Vaccines", count: 0, type: "vaccine" },
                      { name: "Pipeline", count: 0, type: "pipeline" },
                    ]);
                    setProductList([]);
                    const nextUrl = buildGeneralSearchUrl(submittedQuery);
                    const isSameSearchUrl =
                      pathname === scopedSearchPath &&
                      query === submittedQuery &&
                      term === submittedQuery &&
                      category === "general";

                    if (isSameSearchUrl) {
                      // Next.js does not navigate when the destination URL is
                      // unchanged. Run the search directly in that case because
                      // the list above was reset and the URL effect will not fire.
                      await handleSearch(
                        "general",
                        { searchQuery: submittedQuery },
                        forcedTab || activeTab.type || tabs[0].type,
                        selectedOption.sort || sortOptions[0].sort
                      );
                    } else {
                      // Keep the existing skeleton visible during URL navigation,
                      // before the URL effect starts the replacement request.
                      startSearchRequest();
                      router.push(nextUrl);
                      // A changed URL triggers the effect that performs the search.
                    }
                  }
                }}
              >
                <div className="border border-[#EEEEEE] bg-white flex items-center flex-1 p-2 sm:p-3 gap-2 sm:gap-3 rounded-lg relative">
                  <div>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <path
                        d="M14.855 14.145L11.605 10.895H11.57C12.5717 9.71386 13.078 8.19113 12.9828 6.64534C12.8876 5.09955 12.1984 3.65043 11.0593 2.60113C9.92022 1.55182 8.41952 0.983615 6.87112 1.01536C5.32273 1.04711 3.84658 1.67635 2.75147 2.77146C1.65636 3.86657 1.02712 5.34272 0.995372 6.89111C0.963626 8.43951 1.53184 9.9402 2.58114 11.0793C3.63044 12.2184 5.07956 12.9076 6.62535 13.0028C8.17114 13.0979 9.69387 12.5917 10.875 11.59C10.875 11.59 10.875 11.615 10.875 11.625L14.125 14.875C14.1715 14.9219 14.2268 14.959 14.2877 14.9844C14.3486 15.0098 14.414 15.0229 14.48 15.0229C14.546 15.0229 14.6114 15.0098 14.6723 14.9844C14.7332 14.959 14.7885 14.9219 14.835 14.875C14.8863 14.8296 14.9277 14.7742 14.9566 14.7121C14.9856 14.6501 15.0015 14.5828 15.0034 14.5143C15.0052 14.4459 14.993 14.3778 14.9675 14.3143C14.942 14.2507 14.9037 14.1931 14.855 14.145ZM7 12C6.01109 12 5.04439 11.7067 4.22215 11.1573C3.3999 10.6079 2.75904 9.82703 2.3806 8.9134C2.00216 7.99977 1.90314 6.99444 2.09607 6.02453C2.289 5.05463 2.7652 4.16371 3.46446 3.46445C4.16373 2.76519 5.05464 2.28899 6.02455 2.09606C6.99445 1.90313 7.99978 2.00215 8.91341 2.38059C9.82705 2.75903 10.6079 3.39989 11.1573 4.22213C11.7068 5.04438 12 6.01108 12 6.99999C12 7.6566 11.8707 8.30678 11.6194 8.9134C11.3681 9.52003 10.9998 10.0712 10.5355 10.5355C10.0712 10.9998 9.52004 11.3681 8.91341 11.6194C8.30679 11.8707 7.65661 12 7 12Z"
                        fill="#4B4B4B"
                      />
                    </svg>
                  </div>
                  <input
                    ref={inputRef}
                    type="text"
                    onFocus={() => {
                      setIsSuggestionOpen(true);
                    }}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      // debouncedSearch(e.target.value)
                      if (e.target.value == "") {
                        // debouncedSearch(term)
                      }
                    }}
                    value={searchQuery}
                    placeholder="Search medications, devices & more..."
                    className="border-none rounded-lg outline-none w-full text-[14px] sm:text-[16px] font-normal font-poppins"
                  />

                  {isSuggestionOpen && (
                    <div
                      key={searchQuery}
                      ref={dropdownRef}
                      className="z-50 absolute top-10 w-full left-0 notification-scrollbar transform mt-2 p-2 bg-white shadow-md rounded-lg max-h-64 overflow-y-auto border border-gray-200"
                    >
                      <ul>
                        {/* Combine searchHistory and termList */}
                        {(() => {
                          const displayedHistory = uniqueSearchSuggestions(searchHistory
                            .filter((search) => {
                              if (
                                initialTabHint &&
                                (search.type || "").toLowerCase() !== initialTabHint
                              ) {
                                return false;
                              }
                              return search.clickedProduct?.productId && getSearchSuggestionLabel(search, "history").toLowerCase().includes(searchQuery.toLowerCase());
                            }).map((search) => withSearchSuggestionHistoryDisplay(search, termList)), "history");
                          const historyKeys = new Set(displayedHistory.flatMap((search) => getSearchSuggestionIdentities(search, "history")));
                          const historyItems = displayedHistory
                            .map((search, index) => {
                              return (
                                <SearchHistoryItem
                                  clickLoading={setIsClickLoading}
                                  handleClick={handleClick}
                                  key={search._id || `SEARCH_HISTORY_${index}`}
                                  search={search}
                                  type={"history"}
                                />
                              );
                            });
                          // Render termList items with the search icon
                          const termItems = uniqueSearchSuggestions(termList)
                            .filter((item) => !getSearchSuggestionIdentities(item).some((identity) => historyKeys.has(identity)))
                            .map((item, index) => (
                              <SearchHistoryItem
                                clickLoading={setIsClickLoading}
                                setSelectedTermId={setSelectedTermId}
                                search={item}
                                key={getSearchSuggestionIdentity(item) || `TERM_LIST_${index}`}
                                handleClick={handleClick}
                                type={"search"}
                              />
                            ));

                          console.log("INNER", termItems);
                          // Combine and render both lists
                          return (
                            <>
                              {historyItems}
                              {termItems}
                            </>
                          );
                        })()}
                        {!isExactMatch(searchQuery) && searchQuery && (
                          <div
                            onClick={() => {
                              void logKeywordSearch(searchQuery);
                              router.push(buildGeneralSearchUrl(searchQuery));
                              handleSearch("general");
                            }}
                            className="flex items-center justify-between px-3 py-2 cursor-pointer"
                          >
                            {`Search for "${searchQuery}"`}
                            <IoArrowForward />
                          </div>
                        )}
                        {/* Fallback message when there are no results */}
                        {searchHistory.length === 0 &&
                          termList.length === 0 && <p>No results found!</p>}
                      </ul>
                    </div>
                  )}
                </div>

                {/* <div className="flex items-center gap-2 w-[12%] justify-center border cursor-pointer rounded-lg border-[#EEEEEE] p-3">
                  <div>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M10.1248 22.125C9.9259 22.125 9.73513 22.046 9.59448 21.9053C9.45383 21.7647 9.37481 21.5739 9.37481 21.375V14.1637L2.42231 6.5175C2.14862 6.21591 1.96836 5.84136 1.9034 5.43931C1.83844 5.03727 1.89157 4.62501 2.05635 4.25257C2.22113 3.88013 2.49047 3.56353 2.83168 3.34119C3.17289 3.11885 3.5713 3.00032 3.97856 3H20.0211C20.4283 3.00032 20.8267 3.11885 21.1679 3.34119C21.5092 3.56353 21.7785 3.88013 21.9433 4.25257C22.108 4.62501 22.1612 5.03727 22.0962 5.43931C22.0313 5.84136 21.851 6.21591 21.5773 6.5175L14.6248 14.1637V18.375C14.6247 18.4873 14.5994 18.5982 14.5507 18.6995C14.5021 18.8008 14.4313 18.8898 14.3436 18.96L10.5936 21.96C10.4606 22.0667 10.2953 22.1249 10.1248 22.125ZM3.97856 4.5C3.86185 4.50029 3.74773 4.5344 3.65001 4.5982C3.55228 4.66201 3.47515 4.75276 3.42793 4.85949C3.38072 4.96622 3.36545 5.08435 3.38397 5.19958C3.4025 5.31481 3.45402 5.4222 3.53231 5.50875L10.6798 13.3837C10.8023 13.5184 10.8716 13.693 10.8748 13.875V19.815L13.1248 18V13.875C13.1244 13.6878 13.1939 13.5073 13.3198 13.3688L20.4673 5.49375C20.5409 5.40681 20.5885 5.30081 20.6044 5.188C20.6203 5.07519 20.604 4.96018 20.5573 4.85625C20.5107 4.75232 20.4355 4.66372 20.3406 4.60068C20.2458 4.53764 20.135 4.50273 20.0211 4.5H3.97856Z"
                        fill="#4B4B4B"
                      />
                    </svg>
                  </div>
                  <div className="text-[16px] font-normal text-[#4B4B4B] font-poppins">
                    Filters <span className="text-black font-medium ">(1)</span>
                  </div>
                </div> */}

                <button className="bg-[#354EF0] w-full sm:w-auto sm:max-w-max px-6 sm:px-12 py-2.5 sm:py-3 rounded-lg text-white text-[14px] sm:text-[16px] font-normal font-poppins">
                  Search
                </button>
              </form>
            </div>

            <div className={`border-b flex flex-row justify-between items-center border-gray-300 ${!listLoading && !tabs.some((t) => t.count > 0) && productList.length === 0 ? "hidden" : ""}`}>
              <div className="flex overflow-x-auto scrollbar-hide space-x-3 sm:space-x-6 flex-1 min-w-0 pb-0">
                {listLoading && !tabs.some((t) => t.count > 0) ? (
                  // Skeleton tabs while loading
                  <>
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="pb-2 flex-shrink-0">
                        <div className="h-4 w-16 sm:w-20 bg-gray-200 animate-pulse rounded" />
                      </div>
                    ))}
                  </>
                ) : !listLoading && !tabs.some((t) => t.count > 0) && productList.length === 0 ? (
                  // No results at all — hide tabs entirely
                  null
                ) : (
                  tabs
                    .filter((tab) => {
                      // SCOPE LOCK: when the URL hints `?tab=pipeline`
                      // we treat that as a hard scope — hide every
                      // other category tab so the results page is
                      // pipeline-only (the user navigated from the
                      // Emerging & Pipeline search bar, they shouldn't
                      // see Drugs/Devices/OTC/Vaccines tabs). Pure
                      // additive: when `tab` isn't in the URL, the
                      // existing count-based filter still runs.
                      if (initialTabHint) {
                        return tab.type === initialTabHint;
                      }
                      // Only filter after counts are loaded (at least one tab has count > 0)
                      const hasAnyCounts = tabs.some((t) => t.count > 0);
                      if (!hasAnyCounts) return true;
                      return tab.count > 0;
                    })
                    .map((tab) => (
                    <button
                      key={tab.type}
                      onClick={() => {
                        if (tab.type === activeTab.type && !listLoading) return;
                        if (showCachedTab(tab)) return;
                        setActiveTab(tab);
                        // Reset pagination state when tab changes
                        setAfterKey(null);
                        setHasMoreData(true);
                        setProductList([]);
                        if (category == "general") {
                          handleSearch(
                            category,
                            {
                              searchQuery,
                            },
                            tab.type,
                            selectedOption.sort || sortOptions[0].sort
                          );
                        } else {
                          handleSearch(
                            category,
                            null,
                            tab.type,
                            selectedOption.sort || sortOptions[0].sort
                          );
                        }
                      }}
                      className={`pb-2 text-[12px] sm:text-[14px] font-medium whitespace-nowrap flex-shrink-0 ${activeTab.type === tab.type
                        ? "text-blue-600 border-b-2 border-blue-600"
                        : "text-gray-500"
                        }`}
                    >
                      {tab.name}
                    </button>
                  ))
                )}
              </div>

              <div
                ref={dropdownRef}
                className="flex relative items-center cursor-pointer flex-shrink-0"
              >
                <div
                  onClick={() => setIsOpen(!isOpen)}
                  className="flex items-center gap-1 p-2 hover:bg-gray-50 rounded-lg"
                >
                  {/* Mobile: Icon only */}
                  <div className="sm:hidden">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#354EF0"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="4" y1="6" x2="16" y2="6"></line>
                      <line x1="4" y1="12" x2="12" y2="12"></line>
                      <line x1="4" y1="18" x2="8" y2="18"></line>
                      <polyline points="15 15 18 18 21 15"></polyline>
                      <line x1="18" y1="12" x2="18" y2="18"></line>
                    </svg>
                  </div>
                  {/* Desktop: Full text */}
                  <div className="hidden sm:flex items-center gap-1">
                    <span className="text-[#8E8E8E] text-[14px] font-normal font-poppins">Sort by:</span>
                    {/* Pull the label from the live `sortOptions` array
                        so the trigger reflects the pipeline-tab relabel
                        ("Recently Approved" → "Recently Started Trial")
                        immediately, even if `selectedOption` state holds
                        the original label from when it was selected on
                        another tab. */}
                    <span className="text-[#354EF0] text-[14px] font-medium font-poppins">
                      {(sortOptions.find((o) => o.sort === selectedOption.sort) || selectedOption).label}
                    </span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="13"
                      viewBox="0 0 12 13"
                      fill="none"
                    >
                      <path
                        d="M11.7772 3.34763C11.6289 3.19917 11.4531 3.125 11.2499 3.125H0.750056C0.546802 3.125 0.371088 3.19917 0.222628 3.34763C0.0741683 3.49625 0 3.67197 0 3.8751C0 4.07819 0.0741683 4.2539 0.222628 4.4024L5.47257 9.65235C5.6212 9.80081 5.79691 9.87514 6 9.87514C6.20309 9.87514 6.37897 9.80081 6.52731 9.65235L11.7772 4.40236C11.9255 4.2539 12 4.07819 12 3.87506C12 3.67197 11.9255 3.49625 11.7772 3.34763Z"
                        fill="#354EF0"
                      />
                    </svg>
                  </div>
                </div>

                {isOpen && (
                  <div className="absolute w-52 mt-2 right-0 top-6 bg-white border rounded-md shadow-lg z-50">
                    {sortOptions.map((option) => (
                      <button
                        key={option.sort}
                        onClick={() => handleSelect(option)}
                        className={`flex items-center justify-between px-4 py-2.5 text-left w-full hover:bg-gray-100 border-b last:border-b-0 ${selectedOption.sort === option.sort
                          ? "bg-blue-50 text-blue-500"
                          : ""
                          }`}
                      >
                        <div className="flex items-center">
                          <img
                            src={option.icon}
                            alt={`${option.label} icon`}
                            className="w-4 h-4 mr-2"
                          />
                          <div className={`text-[12px] font-medium ${selectedOption.sort === option.sort ? "text-blue-500" : "text-[#1F1F1F]"}`}>
                            {option.label}
                          </div>
                        </div>
                        {selectedOption.sort === option.sort && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#354EF0"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {/* {listLoading && <Loader />} */}
            <div
              id="scroll_container_product"
              className="overflow-y-scroll space-y-2 sm:space-y-4 mt-2 sm:mt-3 medication-conatiner-height"
            >
              {listLoading && productList.length === 0 ? (
                <div className="space-y-2 sm:space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="flex flex-col sm:flex-row sm:items-center bg-white border border-gray-200 rounded-lg p-2.5 sm:p-3 gap-2 sm:gap-3 animate-pulse">
                      <div className="flex items-start sm:items-center gap-2 sm:gap-3 sm:flex-1 sm:min-w-0">
                        <div className="w-[60px] h-[48px] sm:w-[90px] sm:h-[72px] bg-gray-200 rounded-md flex-shrink-0"></div>
                        <div className="flex-1 space-y-2">
                          <div className="h-3.5 sm:h-5 bg-gray-200 rounded-full w-3/4"></div>
                          <div className="h-2.5 sm:h-3.5 bg-gray-100 rounded-full w-1/2"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : productList.length === 0 && !listLoading && !error ? (
                <div className="text-center py-6 sm:py-8 text-gray-500">
                  <div className="flex flex-col items-center gap-2">
                    <svg className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.29-1.009-5.824-2.562M15.9 6.1a7.962 7.962 0 00-7.8 0m15.9 0a7.962 7.962 0 00-7.8 0" />
                    </svg>
                    <p className="text-base sm:text-lg font-medium text-gray-400">No results found</p>
                    <p className="text-xs sm:text-sm text-gray-400 px-4">No {activeTab.name.toLowerCase()} found for your search</p>
                  </div>
                </div>
              ) : error ? (
                error.toLowerCase().includes("search limit") ? (
                  <div className="text-center py-8 sm:py-12 px-4">
                    <div className="flex flex-col items-center gap-3 max-w-sm mx-auto">
                      <div className="w-14 h-14 sm:w-16 sm:h-16 bg-blue-50 rounded-full flex items-center justify-center">
                        <svg className="w-7 h-7 sm:w-8 sm:h-8 text-[#354EF0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                      <p className="text-base sm:text-lg font-semibold text-gray-800 font-poppins">Free search limit reached</p>
                      <p className="text-xs sm:text-sm text-gray-500 font-poppins leading-relaxed">
                        You've used all your free searches. Sign up or log in to get unlimited access to our full database.
                      </p>
                      <div className="flex flex-col sm:flex-row items-center gap-2.5 mt-2 w-full sm:w-auto">
                        <Link href="/login" className="w-full sm:w-auto">
                          <button className="w-full sm:w-auto bg-[#354EF0] text-white text-sm font-medium font-poppins px-8 py-2.5 rounded-lg hover:shadow-lg transition-all">
                            Log In
                          </button>
                        </Link>
                        <Link href="/sign-up" className="w-full sm:w-auto">
                          <button className="w-full sm:w-auto border border-[#354EF0] text-[#354EF0] text-sm font-medium font-poppins px-8 py-2.5 rounded-lg hover:bg-blue-50 transition-all">
                            Sign Up Free
                          </button>
                        </Link>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6 sm:py-8 text-red-500">
                    <div className="flex flex-col items-center gap-2">
                      <svg className="w-10 h-10 sm:w-12 sm:h-12 text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      <p className="text-base sm:text-lg font-medium text-red-400">Error occurred</p>
                      <p className="text-xs sm:text-sm text-red-400 px-4">{error}</p>
                    </div>
                  </div>
                )
              ) : (
                <InfiniteScroll
                  className="h-full"
                  loader={
                    <div className="space-y-2 sm:space-y-3 mt-2">
                      {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="flex flex-col sm:flex-row sm:items-center bg-white border border-gray-200 rounded-lg p-2.5 sm:p-3 gap-2 sm:gap-3 animate-pulse">
                          <div className="flex items-start sm:items-center gap-2 sm:gap-3 sm:flex-1 sm:min-w-0">
                            <div className="w-[60px] h-[48px] sm:w-[90px] sm:h-[72px] bg-gray-200 rounded-md flex-shrink-0"></div>
                            <div className="flex-1 space-y-2">
                              <div className="h-3.5 sm:h-5 bg-gray-200 rounded-full w-3/4"></div>
                              <div className="h-2.5 sm:h-3.5 bg-gray-100 rounded-full w-1/2"></div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  }
                  key={
                    hasMoreData.toString() +
                    productList.length.toString() +
                    "_product_list"
                  }
                  dataLength={productList.length}
                  next={() => {
                    fetchMore();
                  }}
                  hasMore={hasMoreData}
                  scrollableTarget="scroll_container_product"
                >
                  {productList.map((med, index) => {
                    const hasBrands = med?.brands && med.brands.length > 1;

                    // Helper function to render rating/review for a product item
                    const renderRating = (item, idSuffix) => {
                      const globalAverage = item.rating || 0;
                      const reviewCount = item.reviewCount || 0;
                      const formatReviewCount = (num) => num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                      const itemType = item.type || med.type;
                      const itemAlias = item.productAlias || med.productAlias;

                      if (globalAverage > 0 && reviewCount > 0) {
                        return (
                          <Link
                            href={`/products/${itemType}/${itemAlias}#reviews`}
                            onClick={(e) => { e.stopPropagation(); }}
                            className="flex items-center gap-1 sm:gap-1.5 cursor-pointer border-b border-transparent hover:border-gray-400 transition-all whitespace-nowrap pb-0"
                          >
                            <span className="text-sm sm:text-base font-bold text-gray-900 font-poppins">{Number(globalAverage).toFixed(1)}</span>
                            <RatingStar className="w-[12px] h-[12px] sm:w-[14px] sm:h-[14px]" fill="#1E5EFF" clipId={`rating_star_${idSuffix}`} />
                            <span className="text-gray-500 font-poppins text-[11px] sm:text-[13px]">({formatReviewCount(reviewCount)})</span>
                          </Link>
                        );
                      }
                      // Pipeline-flavored copy when this row is a
                      // pre-launch drug — "review" doesn't apply (the
                      // doctor has nothing to review yet), so the
                      // empty-state and CTA both speak to "perspective"
                      // instead. Single-spot branch keeps the styling
                      // identical to the FDA card.
                      const isPipelineRow = (itemType || "").toLowerCase() === "pipeline";
                      const emptyLabel = isPipelineRow ? "No perspectives yet" : "No reviews yet";
                      const ctaLabel = isPipelineRow ? "Share your perspective" : "Be the first to review";
                      return (
                        <div className="flex flex-col items-start sm:items-end gap-0.5">
                          <span className="text-gray-500 font-poppins text-[11px] sm:text-[13px]">{emptyLabel}</span>
                          {users?.type !== "reps" && (
                            <div
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                // Pipeline rows live at /pipeline/:slug
                                // (no `type` path segment) — use the
                                // existing `productHref` helper so this
                                // click follows the same routing rules
                                // as every other product link in this
                                // file. Previously hardcoded to
                                // `/products/pipeline/...` which 404s
                                // because pipeline drugs aren't in the
                                // Products collection.
                                const href = getProductHref(itemType, itemAlias);
                                router.push(
                                  `${href}${href.includes("?") ? "&" : "?"}openReview=true`
                                );
                              }}
                              className="flex items-center gap-1 sm:gap-1.5 cursor-pointer border-b border-transparent hover:border-[#1E5EFF] transition-all whitespace-nowrap pb-0"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#1E5EFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3 sm:w-3.5 sm:h-3.5">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                              <span className="text-[#1E5EFF] font-poppins text-[11px] sm:text-sm">{ctaLabel}</span>
                            </div>
                          )}
                        </div>
                      );
                    };

                    // Helper function to render fav heart button
                    const renderFavButton = (item) => {
                      const itemId = item.id || med.id;
                      const productFamilyIds = item.productFamilyIds || med.productFamilyIds;
                      const isFavourite = isFavouriteProduct(itemId, productFamilyIds);
                      if (!(users?.type === "physician" || users?.type === "clinic")) return null;
                      return (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                onClick={(e) => handleFav(e, { ...med, ...item, id: itemId, productFamilyIds })}
                                className="w-8 h-8 bg-[#F64C4C1A] p-2 mr-1 rounded-md cursor-pointer"
                              >
                                {isFavourite ? <GoHeartFill color="#F64C4C" /> : <GoHeart color="#F64C4C" />}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="text-xs font-medium">{isFavourite ? "Unfavorite" : "Favorite"}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    };

                    // === MULTI-BRAND GROUPED CARD ===
                    if (hasBrands) {
                      // Deduplicate brands by name (case-insensitive) — merge manufacturers
                      const deduped = [];
                      const seen = {};
                      for (const b of med.brands) {
                        const key = (b.brand_name || '').toLowerCase().trim();
                        if (seen[key]) {
                          // Merge: add manufacturer if not already present
                          const existing = seen[key];
                          const mfr = (b.manufacturer_name || '').trim();
                          if (mfr && !existing.manufacturers.some(m => m.name.toLowerCase() === mfr.toLowerCase())) {
                            existing.manufacturers.push({ name: mfr, id: b.id, productAlias: b.productAlias, type: b.type, approvedDate: b.latest_approved_date || null });
                          }
                          // Keep highest rating
                          if ((b.rating || 0) > (existing.rating || 0)) {
                            existing.rating = b.rating;
                            existing.reviewCount = b.reviewCount;
                          }
                        } else {
                          const entry = {
                            ...b,
                            manufacturers: [{ name: (b.manufacturer_name || '').trim(), id: b.id, productAlias: b.productAlias, type: b.type, approvedDate: b.latest_approved_date || null }],
                          };
                          seen[key] = entry;
                          deduped.push(entry);
                        }
                      }

                      // If after dedup there's only 1 unique brand, render as single card with multiple manufacturers
                      if (deduped.length === 1) {
                        const brand = deduped[0];
                        const mfrs = sortManufacturersByApprovalDate(brand.manufacturers.filter(m => m.name));
                        return (
                          <div
                            key={index}
                            className="flex flex-col sm:flex-row sm:items-center bg-[#FDFDFD] scrollbar mt-2 border border-[#EEEEEE] rounded-lg p-2.5 sm:p-3 gap-2 sm:gap-3"
                          >
                            <div className="flex items-start sm:items-center gap-2 sm:gap-3 sm:flex-1 sm:min-w-0">
                              <Link
                                onClick={() => {}}
                                href={getProductHref(brand.type || med.type, brand.productAlias)}
                                className="flex-shrink-0"
                              >
                                <div className="w-[60px] h-[48px] sm:w-[90px] sm:h-[72px]">
                                  <Image height={72} width={90} src={"/Images/placeholder_medicine.svg"} className="w-[60px] h-[48px] sm:w-[90px] sm:h-[72px] aspect-video" alt="Medication" />
                                </div>
                              </Link>
                              <Link
                                onClick={() => {}}
                                href={getProductHref(brand.type || med.type, brand.productAlias)}
                                className="flex-1 min-w-0 product-card-text space-y-0.5"
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <div className="text-[14px] sm:text-[20px] text-[#1F1F1F] font-semibold font-poppins text-ellipsis whitespace-nowrap overflow-hidden min-w-0">
                                    {brand.brand_name || med?.openfda?.brand_name}
                                  </div>
                                  <TypeBadge type={brand.type || med?.type} />
                                  <LifecycleBadge
                                    type={brand.type || med?.type}
                                    lifecycle={med?.lifecycle}
                                  />
                                </div>
                                {/* Pipeline rows carry no FDA brand/generic split and no
                                    FDA manufacturer — show Indication + Sponsor instead. */}
                                {((brand.type || med?.type) || '').toLowerCase() === 'pipeline' ? (
                                  <>
                                    {/* Conditions: comma-separated when more than one; falls back to primaryIndication. */}
                                    <div className="flex items-center gap-1 sm:gap-2">
                                      <div className="text-[11px] sm:text-[14px] font-normal text-[#4B4B4B] font-poppins flex-shrink-0">
                                        {(med?.conditions?.length || 0) > 1 ? 'Indications:' : 'Indication:'}
                                      </div>
                                      <div className="flex-1 capitalize overflow-hidden text-ellipsis whitespace-nowrap text-[11px] sm:text-[14px] font-medium text-[#8E8E8E] font-poppins">
                                        {(med?.conditions || []).length > 0
                                          ? med.conditions.join(', ')
                                          : (med?.primaryIndication || '—')}
                                      </div>
                                    </div>
                                    {/* Sponsors: comma-separated when more than one; falls back to first sponsor. */}
                                    <div className="hidden sm:flex items-center gap-3">
                                      <div className="text-[14px] font-normal text-[#4B4B4B] font-poppins flex-shrink-0">
                                        {(med?.uniqueSponsors?.length || 0) > 1 ? 'Sponsors:' : 'Sponsor:'}
                                      </div>
                                      <div className="capitalize text-[14px] font-medium text-[#8E8E8E] font-poppins text-ellipsis whitespace-nowrap overflow-hidden">
                                        {(med?.uniqueSponsors || []).length > 0
                                          ? med.uniqueSponsors.join(', ')
                                          : (med?.sponsor || mfrs?.[0]?.name || '—')}
                                      </div>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="flex items-center gap-1 sm:gap-2">
                                      <div className="text-[11px] sm:text-[14px] font-normal text-[#4B4B4B] font-poppins flex-shrink-0">Generic:</div>
                                      <div className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[11px] sm:text-[14px] font-medium text-[#8E8E8E] font-poppins">
                                        {med?.generic_name || med?.openfda?.generic_name}
                                      </div>
                                    </div>
                                    <div className="hidden sm:flex items-center gap-3">
                                      <div className="text-[14px] font-normal text-[#4B4B4B] font-poppins flex-shrink-0">
                                        {mfrs.length > 1 ? 'Manufacturers:' : 'Manufacturer Name:'}
                                      </div>
                                      <div className="capitalize text-[14px] font-medium text-[#8E8E8E] font-poppins text-ellipsis whitespace-nowrap overflow-hidden">
                                        {mfrs.map(m => m.name).join(', ')}
                                      </div>
                                    </div>
                                  </>
                                )}
                              </Link>
                            </div>
                            <div className="flex-shrink-0 sm:min-w-[160px] flex flex-row sm:flex-col items-center sm:items-end justify-between sm:justify-center gap-2 self-stretch border-t sm:border-t-0 pt-2 sm:pt-0 mt-1 sm:mt-0">
                              <div className="flex items-center gap-2">
                                <CompareToggle
                                  compact
                                  product={toCompareProduct(brand, med)}
                                />
                                {renderFavButton(brand)}
                              </div>
                              {renderRating(brand, med.id)}
                            </div>
                          </div>
                        );
                      }

                      // Multiple unique brands — show max 2, "+N more" for rest
                      const visibleBrands = deduped.slice(0, 2);
                      const hiddenBrands = deduped.slice(2);
                      const remainingCount = hiddenBrands.length;

                      const renderBrandRow = (brand, bIndex, isLast) => {
                        const mfrs = sortManufacturersByApprovalDate(brand.manufacturers.filter(m => m.name));
                        return (
                          <div
                            key={`${index}-${bIndex}`}
                            className={`flex flex-col sm:flex-row sm:items-center p-2.5 sm:p-3 gap-2 sm:gap-3 ${!isLast ? 'border-b border-dashed border-[#EEEEEE]' : ''}`}
                          >
                            <div className="flex items-start sm:items-center gap-2 sm:gap-3 sm:flex-1 sm:min-w-0">
                              <Link
                                onClick={() => {}}
                                href={getProductHref(brand.type || med.type, brand.productAlias)}
                                className="flex-shrink-0"
                              >
                                <div className="w-[60px] h-[48px] sm:w-[90px] sm:h-[72px]">
                                  <Image height={72} width={90} src={"/Images/placeholder_medicine.svg"} className="w-[60px] h-[48px] sm:w-[90px] sm:h-[72px] aspect-video" alt="Medication" />
                                </div>
                              </Link>
                              <Link
                                onClick={() => {}}
                                href={getProductHref(brand.type || med.type, brand.productAlias)}
                                className="flex-1 min-w-0 product-card-text space-y-0.5"
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <div className="text-[14px] sm:text-[20px] text-[#1F1F1F] font-semibold font-poppins text-ellipsis whitespace-nowrap overflow-hidden min-w-0">
                                    {brand.brand_name || med?.openfda?.brand_name}
                                  </div>
                                  <TypeBadge type={brand.type || med?.type} />
                                  <LifecycleBadge
                                    type={brand.type || med?.type}
                                    lifecycle={med?.lifecycle}
                                  />
                                </div>
                                {/* Pipeline rows carry no FDA brand/generic split and no
                                    FDA manufacturer — show Indication + Sponsor instead. */}
                                {((brand.type || med?.type) || '').toLowerCase() === 'pipeline' ? (
                                  <>
                                    {/* Conditions: comma-separated when more than one; falls back to primaryIndication. */}
                                    <div className="flex items-center gap-1 sm:gap-2">
                                      <div className="text-[11px] sm:text-[14px] font-normal text-[#4B4B4B] font-poppins flex-shrink-0">
                                        {(med?.conditions?.length || 0) > 1 ? 'Indications:' : 'Indication:'}
                                      </div>
                                      <div className="flex-1 capitalize overflow-hidden text-ellipsis whitespace-nowrap text-[11px] sm:text-[14px] font-medium text-[#8E8E8E] font-poppins">
                                        {(med?.conditions || []).length > 0
                                          ? med.conditions.join(', ')
                                          : (med?.primaryIndication || '—')}
                                      </div>
                                    </div>
                                    {/* Sponsors: comma-separated when more than one; falls back to first sponsor. */}
                                    <div className="hidden sm:flex items-center gap-3">
                                      <div className="text-[14px] font-normal text-[#4B4B4B] font-poppins flex-shrink-0">
                                        {(med?.uniqueSponsors?.length || 0) > 1 ? 'Sponsors:' : 'Sponsor:'}
                                      </div>
                                      <div className="capitalize text-[14px] font-medium text-[#8E8E8E] font-poppins text-ellipsis whitespace-nowrap overflow-hidden">
                                        {(med?.uniqueSponsors || []).length > 0
                                          ? med.uniqueSponsors.join(', ')
                                          : (med?.sponsor || mfrs?.[0]?.name || '—')}
                                      </div>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="flex items-center gap-1 sm:gap-2">
                                      <div className="text-[11px] sm:text-[14px] font-normal text-[#4B4B4B] font-poppins flex-shrink-0">Generic:</div>
                                      <div className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[11px] sm:text-[14px] font-medium text-[#8E8E8E] font-poppins">
                                        {med?.generic_name || med?.openfda?.generic_name}
                                      </div>
                                    </div>
                                    <div className="hidden sm:flex items-center gap-3">
                                      <div className="text-[14px] font-normal text-[#4B4B4B] font-poppins flex-shrink-0">
                                        {mfrs.length > 1 ? 'Manufacturers:' : 'Manufacturer Name:'}
                                      </div>
                                      <div className="capitalize text-[14px] font-medium text-[#8E8E8E] font-poppins text-ellipsis whitespace-nowrap overflow-hidden">
                                        {mfrs.map(m => m.name).join(', ')}
                                      </div>
                                    </div>
                                  </>
                                )}
                              </Link>
                            </div>
                            <div className="flex-shrink-0 sm:min-w-[160px] flex flex-row sm:flex-col items-center sm:items-end justify-between sm:justify-center gap-2 self-stretch border-t sm:border-t-0 pt-2 sm:pt-0 mt-1 sm:mt-0">
                              <div className="flex items-center gap-2">
                                <CompareToggle
                                  compact
                                  product={toCompareProduct(brand, med)}
                                />
                                {renderFavButton(brand)}
                              </div>
                              {renderRating(brand, `${med.id}_brand_${bIndex}`)}
                            </div>
                          </div>
                        );
                      };

                      return (
                        <div
                          key={index}
                          className="bg-[#FDFDFD] scrollbar mt-2 border border-[#EEEEEE] rounded-lg overflow-hidden"
                        >
                          {visibleBrands.map((brand, bIndex) =>
                            renderBrandRow(brand, bIndex, bIndex === visibleBrands.length - 1 && remainingCount <= 0)
                          )}
                          {remainingCount > 0 && (
                            <div className="flex items-center justify-center gap-1.5 py-2.5 sm:py-3 border-t border-[#EEEEEE] px-3 flex-wrap">
                              <span className="text-[12px] sm:text-[13px] font-medium text-[#8E8E8E] font-poppins flex-shrink-0">
                                +{remainingCount} more:
                              </span>
                              {hiddenBrands.map((b, i) => (
                                <span key={i} className="flex items-center">
                                  <Link
                                    href={getProductHref(b.type || med.type, b.productAlias)}
                                    onClick={(e) => { e.stopPropagation(); }}
                                    className="text-[12px] sm:text-[13px] font-medium text-[#354EF0] font-poppins hover:underline underline-offset-2"
                                  >
                                    {b.brand_name}
                                  </Link>
                                  {i < hiddenBrands.length - 1 && <span className="text-[#8E8E8E] text-[12px] mr-1">,</span>}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    }

                    // === SINGLE-BRAND CARD (unchanged layout) ===
                    return (
                      <div
                        key={index}
                        className="flex flex-col sm:flex-row sm:items-center bg-[#FDFDFD] scrollbar mt-2 border border-[#EEEEEE] rounded-lg p-2.5 sm:p-3 gap-2 sm:gap-3"
                      >
                        {/* Mobile: Image + Title Row */}
                        <div className="flex items-start sm:items-center gap-2 sm:gap-3 sm:flex-1 sm:min-w-0">
                          {/* Image Section - Fixed Width */}
                          <Link
                            onClick={() => {}}
                            href={getProductHref(med.type, med.productAlias)}
                            className="flex-shrink-0"
                          >
                            <div className="w-[60px] h-[48px] sm:w-[90px] sm:h-[72px]">
                              <Image
                                height={72}
                                width={90}
                                src={"/Images/placeholder_medicine.svg"}
                                className="w-[60px] h-[48px] sm:w-[90px] sm:h-[72px] aspect-video"
                                alt="Medication"
                              />
                            </div>
                          </Link>

                          {/* Product Info Section - Takes Remaining Width */}
                          <Link
                            onClick={() => {}}
                            href={getProductHref(med.type, med.productAlias)}
                            className="flex-1 min-w-0 product-card-text space-y-0.5"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <div className="text-[14px] sm:text-[20px] text-[#1F1F1F] font-semibold font-poppins text-ellipsis whitespace-nowrap overflow-hidden min-w-0">
                                {med?.openfda?.brand_name ??
                                  med?.openfda?.generic_name}
                              </div>
                              <TypeBadge type={med?.type} />
                              <LifecycleBadge type={med?.type} lifecycle={med?.lifecycle} />
                            </div>
                            {(med?.type || '').toLowerCase() === 'pipeline' ? (
                              <>
                                <div className="flex items-center gap-1 sm:gap-2">
                                  <div className="text-[11px] sm:text-[14px] font-normal text-[#4B4B4B] font-poppins flex-shrink-0">
                                    {(med?.conditions?.length || 0) > 1 ? 'Indications:' : 'Indication:'}
                                  </div>
                                  <div className="flex-1 capitalize overflow-hidden text-ellipsis whitespace-nowrap text-[11px] sm:text-[14px] font-medium text-[#8E8E8E] font-poppins">
                                    {(med?.conditions || []).length > 0
                                      ? med.conditions.join(', ')
                                      : (med?.primaryIndication || '—')}
                                  </div>
                                </div>
                                <div className="hidden sm:flex items-center gap-3">
                                  <div className="text-[14px] font-normal text-[#4B4B4B] font-poppins flex-shrink-0">
                                    {(med?.uniqueSponsors?.length || 0) > 1 ? 'Sponsors:' : 'Sponsor:'}
                                  </div>
                                  <div className="capitalize text-[14px] font-medium text-[#8E8E8E] font-poppins text-ellipsis whitespace-nowrap overflow-hidden">
                                    {(med?.uniqueSponsors || []).length > 0
                                      ? med.uniqueSponsors.join(', ')
                                      : (med?.sponsor || med?.openfda?.manufacturer_name || '—')}
                                  </div>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="flex items-center gap-1 sm:gap-2">
                                  <div className="text-[11px] sm:text-[14px] font-normal text-[#4B4B4B] font-poppins flex-shrink-0">
                                    Generic:
                                  </div>
                                  <div className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[11px] sm:text-[14px] font-medium text-[#8E8E8E] font-poppins">
                                    {med?.openfda?.generic_name}
                                  </div>
                                </div>
                                <div className="hidden sm:flex items-center gap-3">
                                  <div className="text-[14px] font-normal text-[#4B4B4B] font-poppins flex-shrink-0">
                                    Manufacturer Name:
                                  </div>
                                  <div className="capitalize text-[14px] font-medium text-[#8E8E8E] font-poppins text-ellipsis whitespace-nowrap overflow-hidden">
                                    {med?.openfda?.manufacturer_name}
                                  </div>
                                </div>
                              </>
                            )}
                          </Link>
                        </div>

                        {/* Heart & Rating Section - Horizontal on mobile, Vertical on desktop */}
                        <div className="flex-shrink-0 sm:min-w-[160px] flex flex-row sm:flex-col items-center sm:items-end justify-between sm:justify-center gap-2 self-stretch border-t sm:border-t-0 pt-2 sm:pt-0 mt-1 sm:mt-0">
                          <div className="flex items-center gap-2">
                            <CompareToggle
                              compact
                              product={toCompareProduct(med)}
                            />
                            {renderFavButton(med)}
                          </div>
                          {renderRating(med, med.id)}
                        </div>
                      </div>
                    );
                  })}
                </InfiniteScroll>
              )}
            </div>
          </div>
        </>
      </div>
      {brandsModalData && (
        <ProductsListingPopUp
          filteredList={brandsModalData.brands.map((b) => ({
            openfda: {
              brand_name: b.brand_name,
              generic_name: brandsModalData.generic_name || brandsModalData?.openfda?.generic_name,
              manufacturer_name: b.manufacturer_name,
            },
            type: b.type || brandsModalData.type,
            productAlias: b.productAlias,
          }))}
          onClose={() => setBrandsModalData(null)}
        />
      )}
    </>
  );
};

export default SearchFilterMedication;
